/**
 * The soft-degrade ladder — `organizations.billing_status` as a function of facts, pure.
 *
 * Every trigger (a Stripe event, the grace clock, the rollup) asks this function and writes
 * only when its answer differs, so the order events arrive in cannot matter — Drift Log §U60,
 * ruled 2026-10-28. `packages/db/src/billing/transition.ts` is the one caller that writes.
 */
import { ENTITLING_SUBSCRIPTION_STATUSES } from "./billing.js";
import { PLANS } from "./plans.js";
import { meterState } from "./usage.js";
/** A failed payment's grace, TR §3.6. Degraded once this much time has passed. */
export const GRACE_DAYS = 21;
const GRACE_MS = GRACE_DAYS * 24 * 60 * 60 * 1000;
/** The tiers whose fair-use ceiling degrades — Q-P3, and the ruling of 2026-10-28. */
export const FAIR_USE_DEGRADE_TIERS = ["launch", "starter"];
/** Statuses where the subscription exists and its money is not being collected. */
const OWED_SUBSCRIPTION_STATUSES = [
    "past_due",
    "unpaid",
    "paused",
];
export function paymentStanding(statuses) {
    if (statuses.some((s) => ENTITLING_SUBSCRIPTION_STATUSES.includes(s)))
        return "paid";
    if (statuses.some((s) => OWED_SUBSCRIPTION_STATUSES.includes(s)))
        return "owed";
    if (statuses.includes("canceled"))
        return "ended";
    return "none";
}
/** Over the tier's fair-use ceiling, on a tier where that degrades. `meterState`'s `>`. */
export function fairUseOver(tier, resolverRequests) {
    return (FAIR_USE_DEGRADE_TIERS.includes(tier) &&
        resolverRequests !== null &&
        meterState(resolverRequests, PLANS[tier].fairUsePerMonth) === "over-limit");
}
/**
 * The fair-use figure a rollup just crossed, or `null` for no crossing — the operator notice
 * `apps/worker/src/jobs/usage-rollup.ts` owes, Drift Log §U59.
 *
 * `previous <= limit < counted`, which fires once per organisation per window and needs no
 * dedup column: the count is absolute inside a window, so a retry at the same clock recounts
 * to the same value and `previous === counted`. The outbox could not have been the dedup
 * either — `drizzle/0038`'s trigger nulls `payload` on send, so "already sent for window W"
 * is unreadable there by construction. Drift Log §U65a.
 *
 * Launch and Starter answer `null`: {@link FAIR_USE_DEGRADE_TIERS} degrades them already and
 * the tenant's own dashboard shows it. Growth and Scale change no visible state at all, which
 * is the gap the observation names — Cameron's ruling, Fable plan §6 Q3.
 *
 * `previousResolverRequests` is `null` when the window held no row, and counts as 0.
 */
export function fairUseCrossing(tier, previousResolverRequests, resolverRequests) {
    if (FAIR_USE_DEGRADE_TIERS.includes(tier))
        return null;
    const limit = PLANS[tier].fairUsePerMonth;
    if (limit === null)
        return null;
    const previous = previousResolverRequests ?? 0;
    return previous <= limit && limit < resolverRequests ? limit : null;
}
/** When an organisation in grace degrades, if its payment stays owed. */
export function graceEndsAt(graceSince) {
    return new Date(graceSince.getTime() + GRACE_MS);
}
export function deriveBilling(facts) {
    const owed = facts.standing === "owed";
    const graceSince = owed ? (facts.graceSince ?? facts.now) : null;
    const payment = graceSince !== null && facts.now.getTime() >= graceEndsAt(graceSince).getTime();
    const fairUse = facts.standing !== "ended" && fairUseOver(facts.tier, facts.resolverRequests);
    const status = facts.standing === "ended"
        ? "churned"
        : payment || fairUse
            ? "degraded"
            : owed
                ? "grace"
                : "active";
    return { status, graceSince, degradedBy: { payment, fairUse } };
}
//# sourceMappingURL=ladder.js.map